import {
  ENDED_STATUSES,
  type AgentMessagePayload,
  type AgentQuestionPayload,
  type ApprovalDecidedPayload,
  type ApprovalRequestedPayload,
  type ConsultantMessagePayload,
  type ConversationEndedPayload,
  type ConversationEvent,
  type ConversationStartedPayload,
  type ConversationStatus,
  type ErrorPayload,
  type LoginCheckedPayload,
  type ModelCallPayload,
  type RefusalPayload,
  type RowCompletedPayload,
  type RowOutcome,
  type RowPlanApprovedPayload,
  type RowStartedPayload,
  type SaveApprovalRevokedPayload,
  type ScreenshotTakenPayload,
  type ScriptRecordedPayload,
  type SessionClosedPayload,
  type TargetOpenedPayload,
  type TurnCompletedPayload,
} from "@/api/agent-conversation";
import type {
  ConversationAction,
  ConversationItem,
  ConversationScreenState,
  NoticeCode,
  PendingMessage,
  SummaryItem,
} from "@/components/agents/conversation/types";
import { ID_PAUSE_CODES } from "@/components/agents/conversation/conversationTexts";

/**
 * Redutor puro da conversa: eventos do log → itens de tela (spec §10).
 *
 * O log é a fonte da verdade (spec §6.2): a tela não guarda nada que não saia dele,
 * fora as mensagens que o consultor acabou de mandar e que ainda não viraram
 * evento (`pending`). Evento com `seq` já aplicado é descartado, porque stream,
 * polling e a reconexão (que reenvia a pendência) se sobrepõem.
 *
 * Textos fixos não são montados aqui: aviso e recusa guardam o código, e a tela
 * traduz no idioma da conta. O que é técnico (`message` em inglês) vai para
 * blocos de log, agrupando os eventos técnicos seguidos num bloco só.
 */

export const INITIAL_CONVERSATION_STATE: ConversationScreenState = {
  runId: null,
  items: [],
  lastSeq: 0,
  status: null,
  terminalPresented: false,
  activity: null,
  activeTurnId: null,
  progress: null,
  pending: [],
  scriptRun: false,
  scriptWaitingForId: false,
  executor: null,
};

export function isConversationEnded(
  status: ConversationStatus | null,
): status is ConversationEndedPayload["status"] {
  return status !== null && ENDED_STATUSES.has(status);
}

/** O servidor valida cada payload pelo modelo do tipo antes de gravar (Parte C). */
function payloadOf<T>(event: ConversationEvent): T {
  return event.payload as unknown as T;
}

function push(state: ConversationScreenState, item: ConversationItem): ConversationScreenState {
  return { ...state, items: [...state.items, item] };
}

function appendLog(state: ConversationScreenState, event: ConversationEvent): ConversationScreenState {
  const line = { seq: event.seq, level: event.level, message: event.message };
  const last = state.items[state.items.length - 1];
  if (last?.kind === "log") {
    return {
      ...state,
      items: [...state.items.slice(0, -1), { ...last, lines: [...last.lines, line] }],
    };
  }
  return push(state, { kind: "log", seq: event.seq, lines: [line] });
}

function notice(
  state: ConversationScreenState,
  event: ConversationEvent,
  code: NoticeCode,
  params: Record<string, string> = {},
): ConversationScreenState {
  return push(state, { kind: "notice", seq: event.seq, code, params });
}

/** Conversa encerrada não volta atrás por evento tardio. */
function moveTo(
  state: ConversationScreenState,
  status: ConversationStatus,
  resume = false,
): ConversationStatus | null {
  if (isConversationEnded(state.status)) return state.status;
  if (state.status === "paused" && !resume && !isConversationEnded(status)) return "paused";
  return status;
}

const ENDED_NOTICE: Record<ConversationEndedPayload["status"], NoticeCode> = {
  completed: "conversation_completed",
  interrupted: "conversation_interrupted",
  failed: "conversation_failed",
};

/**
 * Última conclusão de cada linha que a tela já tem. Linha refeita (recuperação
 * técnica) vale pela última: é a regra do progresso e do resumo.
 */
function rowOutcomes(items: ConversationItem[]): RowOutcome[] {
  const outcomes = new Map<number, RowOutcome>();
  for (const item of items) {
    if (item.kind === "row") outcomes.set(item.rowIndex, item.outcome);
  }
  return Array.from(outcomes.values());
}

/**
 * A pendência morre com a sessão: depois de um restart nenhum evento decide a
 * aprovação, e a rota responde 409 `approval_not_pending`. O cartão sem decisão passa a
 * expirado para não parecer pendente; a retomada que pedir de novo traz outro
 * `approval_requested`, e ele vira outro cartão.
 */
function expirePendingApprovals(items: ConversationItem[]): ConversationItem[] {
  return items.map(
    (item): ConversationItem =>
      item.kind === "approval" && item.decision === null ? { ...item, decision: "expired" } : item,
  );
}

/** Resumo do encerramento (spec §7.1, fase 4), contado das linhas que a tela já tem. */
function summaryOf(items: ConversationItem[], seq: number): SummaryItem {
  const outcomes = rowOutcomes(items);
  const count = (outcome: RowOutcome) => outcomes.filter((value) => value === outcome).length;
  return {
    kind: "summary",
    seq,
    created: count("created"),
    alreadyExisted: count("already_existed"),
    failed: count("failed"),
    skipped: count("skipped"),
  };
}

function applyEvent(state: ConversationScreenState, event: ConversationEvent): ConversationScreenState {
  switch (event.event_type) {
    case "conversation_started":
      return appendLog(
        {
          ...state,
          status: moveTo(state, "running"),
          scriptRun: payloadOf<ConversationStartedPayload>(event).details?.script_mode === "script",
        },
        event,
      );
    case "script_deviation": {
      // O roteiro desviou: o agente assume e pergunta ao consultor, então o campo volta,
      // e o chat diz por que a tag do roteiro saiu.
      const step = (event.payload as { step_index?: number | null }).step_index;
      return notice(
        appendLog({ ...state, scriptRun: false, scriptWaitingForId: false }, event),
        event,
        "script_deviation",
        step === null || step === undefined ? {} : { step: String(step + 1) },
      );
    }
    case "session_opened":
    case "session_reattached": {
      const waiting = state.activeTurnId === null && state.items.some(
        (item) => item.kind === "question" && !item.answered && !item.superseded,
      );
      return appendLog(
        {
          ...state,
          status: moveTo(state, waiting ? "waiting" : "running", true),
          activity: waiting ? "waiting" : "thinking",
        },
        event,
      );
    }
    case "turn_started":
      return appendLog(
        {
          ...state,
          status: moveTo(state, "running", true),
          activity: "thinking",
          activeTurnId: event.turn_id,
        },
        event,
      );
    case "turn_completed": {
      const { status, cause } = payloadOf<TurnCompletedPayload>(event);
      const next = {
        ...state,
        status: moveTo(state, "waiting"),
        activity: null,
        activeTurnId: state.activeTurnId === event.turn_id ? null : state.activeTurnId,
      };
      if (status === "interrupted" && cause === "stop") return notice(next, event, "turn_interrupted");
      if (status === "failed") return notice(next, event, "turn_failed");
      return appendLog(next, event);
    }
    case "consultant_message": {
      const payload = payloadOf<ConsultantMessagePayload>(event);
      // Somente a pergunta vigente pode ser respondida, como no fold Python.
      const items: ConversationItem[] = state.items.map((item) =>
        item.kind === "question" && !item.answered && !item.superseded && payload.text.trim()
          && (payload.reply_to == null || (payload.reply_to === item.questionId
            && (item.format !== "options" || item.choices.includes(payload.text.trim()))))
          ? { ...item, answered: true }
          : item,
      );
      return {
        ...state,
        status: moveTo(state, "running"),
        scriptRun: state.scriptRun || state.scriptWaitingForId,
        scriptWaitingForId: false,
        items: [...items, {
          kind: "consultant_message",
          seq: event.seq,
          text: payload.text,
          clientMessageId: payload.client_message_id,
          createdAt: event.created_at,
        }],
        pending: state.pending.some((message) => message.clientMessageId === payload.client_message_id)
          ? withPendingState(state.pending, payload.client_message_id, "queued")
          : [...state.pending, {
            clientMessageId: payload.client_message_id,
            text: payload.text,
            replyTo: payload.reply_to ?? null,
            state: "queued",
            errorCode: null,
            retryable: false,
          }],
      };
    }
    case "model_call": {
      const consumed = new Set(payloadOf<ModelCallPayload>(event).consumed_message_ids ?? []);
      return appendLog({
        ...state,
        pending: state.pending.filter((message) => !consumed.has(message.clientMessageId)),
      }, event);
    }
    case "save_approval_revoked": {
      const payload = payloadOf<SaveApprovalRevokedPayload>(event);
      return appendLog({
        ...state,
        items: state.items.map((item) =>
          item.kind === "approval" && item.approvalKind === "save"
            && item.approvalId === payload.approval_id && item.dataHash === payload.data_hash
            && item.decision === "approve" ? { ...item, decision: "expired" } : item,
        ),
      }, event);
    }
    case "error": {
      const payload = payloadOf<ErrorPayload>(event);
      // O roteiro espera o consultor editar o ID: o campo abre para a resposta e volta a
      // fechar quando ela chega e o roteiro segue sozinho.
      const idWait = state.scriptRun && ID_PAUSE_CODES.has(payload.code ?? "");
      return notice(
        idWait ? { ...state, scriptRun: false, scriptWaitingForId: true, activity: "waiting" } : state,
        event,
        "driver_error",
        { code: payload.code ?? "", detail: payload.message },
      );
    }
    case "agent_message":
      return push(state, {
        kind: "agent_message",
        seq: event.seq,
        text: payloadOf<AgentMessagePayload>(event).text,
        createdAt: event.created_at,
      });
    case "agent_question": {
      const payload = payloadOf<AgentQuestionPayload>(event);
      return push(
        {
          ...state,
          // Pergunta do agente é conversa: sem o campo aberto ela não teria resposta.
          scriptRun: false,
          scriptWaitingForId: false,
          status: moveTo(state, "waiting"),
          activity: null,
          items: state.items.map((item) =>
            item.kind === "question" && !item.answered && !item.superseded
              ? { ...item, superseded: true } : item,
          ),
        },
        {
          kind: "question",
          seq: event.seq,
          questionId: payload.question_id,
          text: payload.text,
          format: payload.format,
          choices: payload.choices ?? [],
          mappingFields: payload.mapping_fields ?? [],
          answered: false,
          superseded: false,
        },
      );
    }
    case "refusal":
      return push(state, {
        kind: "refusal",
        seq: event.seq,
        code: payloadOf<RefusalPayload>(event).code,
      });
    case "approval_requested": {
      const payload = payloadOf<ApprovalRequestedPayload>(event);
      return push(
        { ...state, status: moveTo(state, "waiting"), activity: "waiting" },
        {
          kind: "approval",
          seq: event.seq,
          approvalId: payload.approval_id,
          dataHash: payload.data_hash,
          approvalKind: payload.kind,
          summary: payload.summary,
          data: payload.data,
          decision: isConversationEnded(state.status) ? "expired" : null,
          comment: null,
        },
      );
    }
    case "approval_decided": {
      const payload = payloadOf<ApprovalDecidedPayload>(event);
      // Só o cartão ainda pendente: o mesmo `approval_id` pedido de novo na retomada é
      // outro cartão, e o antigo já fechou com a sessão.
      const items: ConversationItem[] = state.items.map((item) =>
        item.kind === "approval" &&
        item.approvalId === payload.approval_id &&
        item.decision === null
          ? { ...item, decision: payload.decision, comment: payload.comment ?? null }
          : item,
      );
      // Expirada pela ociosidade: o agente não volta a trabalhar por causa dela.
      if (payload.decision === "expired") return { ...state, items };
      // Recusa de aprovação fora de turno (o seguir linhas do condutor): ninguém retoma
      // o trabalho, o agente espera a pessoa escrever e não emite mais nada. Voltar a
      // `running` aqui fecharia o campo até a pausa por ociosidade.
      if (payload.decision === "reject" && event.turn_id === null && state.activeTurnId === null) {
        return { ...state, items, status: moveTo(state, "waiting"), activity: "waiting" };
      }
      return { ...state, items, status: moveTo(state, "running"), activity: "thinking" };
    }
    case "screenshot_taken":
      return push(state, {
        kind: "screenshot",
        seq: event.seq,
        logId: event.id,
        screenshot: payloadOf<ScreenshotTakenPayload>(event).screenshot,
        message: event.message,
        createdAt: event.created_at,
      });
    case "row_plan_approved": {
      const { rows } = payloadOf<RowPlanApprovedPayload>(event);
      return appendLog(
        { ...state, progress: { total: rows.length, done: 0, failed: 0, current: null } },
        event,
      );
    }
    case "row_started": {
      const { row_index } = payloadOf<RowStartedPayload>(event);
      // O condutor roda as linhas fora de turno: sem isto a tela ficaria em
      // "esperando" com o script no navegador, e Parar não valeria. O que vem depois da
      // linha (próxima linha, aprovação de seguir, turno ou encerramento) muda o status.
      return appendLog(
        {
          ...state,
          status: moveTo(state, "running"),
          activity: "acting",
          progress: state.progress && { ...state.progress, current: row_index },
        },
        event,
      );
    }
    case "row_completed": {
      const payload = payloadOf<RowCompletedPayload>(event);
      const next = push(state, {
        kind: "row",
        seq: event.seq,
        rowIndex: payload.row_index,
        outcome: payload.outcome,
        path: payload.path,
        sapMessage: payload.sap_message ?? null,
      });
      if (!state.progress) return next;
      const outcomes = rowOutcomes(next.items);
      return {
        ...next,
        progress: {
          ...state.progress,
          done: outcomes.length,
          failed: outcomes.filter((outcome) => outcome === "failed").length,
          current: null,
        },
      };
    }
    case "script_recorded": {
      const { code, version } = payloadOf<ScriptRecordedPayload>(event);
      return notice(state, event, "script_recorded", { code, version: String(version) });
    }
    case "session_closed": {
      const { reason } = payloadOf<SessionClosedPayload>(event);
      const closed = { ...state, activeTurnId: null, items: expirePendingApprovals(state.items) };
      if (reason === "idle" || reason === "restart") {
        const next = { ...closed, status: moveTo(state, "paused"), activity: null };
        return notice(next, event, reason === "idle" ? "session_paused_idle" : "session_restarted");
      }
      return appendLog({
        ...closed,
        status: reason === "ended" ? state.status : moveTo(state, "paused"),
      }, event);
    }
    case "login_checked": {
      const payload = payloadOf<LoginCheckedPayload>(event);
      if (payload.path === "failed") {
        return notice(state, event, "login_failed", { detail: payload.detail ?? "" });
      }
      return appendLog(state, event);
    }
    case "target_opened": {
      const payload = payloadOf<TargetOpenedPayload>(event);
      if (!payload.editable) {
        return notice(state, event, "target_read_only", { reason: payload.blocked_reason ?? "" });
      }
      return appendLog(state, event);
    }
    case "conversation_ended": {
      if (state.terminalPresented) return appendLog(state, event);
      const status = isConversationEnded(state.status)
        ? state.status
        : payloadOf<ConversationEndedPayload>(event).status;
      const ended = notice(
        {
          ...state,
          items: expirePendingApprovals(state.items),
          status,
          terminalPresented: true,
          activity: null,
        },
        event,
        ENDED_NOTICE[status],
      );
      // O resumo não é mensagem do agente: a tela o monta das linhas, e só
      // quando houve recorte aprovado — sem linhas, o aviso basta.
      return state.progress ? push(ended, summaryOf(ended.items, event.seq)) : ended;
    }
    default:
      // Leitura de tela, chamada ao modelo, ferramenta, erro e o que o servidor
      // inventar depois: log técnico, com a frase em inglês do próprio evento.
      return appendLog(state, event);
  }
}

function applyEvents(
  state: ConversationScreenState,
  events: ConversationEvent[],
): ConversationScreenState {
  let next = state;
  for (const event of [...events].sort((a, b) => a.seq - b.seq)) {
    if (event.seq <= next.lastSeq) continue;
    next = applyEvent({ ...next, runId: next.runId ?? event.run_id, lastSeq: event.seq }, event);
  }
  const idle = isConversationEnded(next.status) || next.status === "paused";
  const settled = next.scriptRun && isConversationEnded(next.status) ? { ...next, scriptRun: false, scriptWaitingForId: false } : next;
  return idle ? { ...settled, activeTurnId: null, activity: null } : settled;
}

function withPendingState(
  pending: PendingMessage[],
  clientMessageId: string,
  state: PendingMessage["state"],
): PendingMessage[] {
  return pending.map((message) =>
    message.clientMessageId === clientMessageId ? { ...message, state, errorCode: null, retryable: false } : message,
  );
}

export function conversationReducer(
  state: ConversationScreenState,
  action: ConversationAction,
): ConversationScreenState {
  switch (action.type) {
    case "reset":
      return INITIAL_CONVERSATION_STATE;
    case "view_loaded": {
      const sameConversation = state.runId === null || state.runId === action.view.run_id;
      const base = sameConversation ? state : INITIAL_CONVERSATION_STATE;
      const next = applyEvents({ ...base, runId: action.view.run_id }, action.view.events);
      // Antes do encerramento, o boot pode pausar sem evento; depois, vale o primeiro fim.
      const authoritative = action.view.status === "paused" || isConversationEnded(action.view.status);
      // O `waiting` do servidor também vale quando nada em curso o contradiz (sem turno
      // aberto e sem linha rodando): nem toda espera tem um evento que a anuncie.
      const waitingNow =
        action.view.status === "waiting" &&
        next.activeTurnId === null &&
        next.progress?.current == null;
      const status = authoritative
        ? moveTo(next, action.view.status)
        : waitingNow
          ? moveTo(next, "waiting")
          : next.status ?? action.view.status;
      const ended = isConversationEnded(status);
      const idle = ended || status === "paused";
      return {
        ...next,
        status,
        scriptRun: next.scriptRun && !ended,
        items: ended ? expirePendingApprovals(next.items) : next.items,
        activity: idle
          ? null
          : waitingNow && status === "waiting" && next.activity !== "waiting"
            ? null
            : next.activity,
        activeTurnId: idle ? null : next.activeTurnId,
        // O polling não traz o dono: sem `requested_by` vale o que a primeira leitura guardou.
        executor: action.view.requested_by
          ? { id: action.view.requested_by, name: action.view.requested_by_name ?? null }
          : next.executor,
      };
    }
    case "executor_loaded":
      // O dono do run não envelhece: vale mesmo quando a leitura que o trouxe foi descartada
      // por trazer eventos velhos. Só se aplica ao run que a tela já mostra (ou ainda sem run).
      if (state.runId !== null && state.runId !== action.runId) return state;
      return { ...state, executor: { id: action.requestedBy, name: action.requestedByName } };
    case "events_received":
      return applyEvents(state, action.events);
    case "agent_status_received":
      if (isConversationEnded(state.status) || state.status === "paused") return state;
      return { ...state, activity: action.activity };
    case "message_sending": {
      // Com o turno rodando, a mensagem entra na fila do turno (steer) e só é lida
      // antes da próxima chamada ao modelo: a tela diz isso já no envio.
      const initial: PendingMessage["state"] = state.status === "running" ? "queued" : "sending";
      const known = state.pending.some(
        (message) => message.clientMessageId === action.clientMessageId,
      );
      return {
        ...state,
        pending: known
          ? withPendingState(state.pending, action.clientMessageId, initial)
          : [
              ...state.pending,
              {
                clientMessageId: action.clientMessageId,
                text: action.text,
                replyTo: action.replyTo,
                state: initial,
                errorCode: null,
                retryable: false,
              },
            ],
      };
    }
    case "message_routed":
      // `started` e `duplicate` seguem "enviando" até o evento chegar.
      if (action.outcome !== "steered") return state;
      return { ...state, pending: withPendingState(state.pending, action.clientMessageId, "queued") };
    case "message_failed":
      return { ...state, pending: state.pending.map((message) => message.clientMessageId === action.clientMessageId
        ? { ...message, state: "failed", errorCode: action.errorCode, retryable: action.retryable }
        : message) };
  }
}
